import { Component, Element, Prop, State } from "@stencil/core";

import { Bundle, bundleForLanguage } from "../../services/i18n";
import { TLanguage } from "../../types";
import { HeaderInterface } from "../../services/header-interface/header-interface.service";
import { devMode } from "../../services/debug";
import { getValidatedValueFromLocalStorage } from "./basket-local-storage-utils";

const ICON = "icon-shopping-trolley";

const SLOT_DATASET = {
  trackLocation: "header",
  trackAction: "open",
  trackCategory: "l1-icon",
  trackLabel: "basket",
};

const CART_CONFIG_LOCAL_STORAGE_KEY = "oce_basket_url";
const CART_CONFIG_UPDATED = "cartConfigUpdated";

@Component({
  tag: "scs-basket",
})
export class BasketComponent {
  private i18n!: Bundle;
  private sdxHeader!: HeaderInterface;

  @Element()
  private host!: HTMLElement;

  /** will be used to identify the correct default basket page path e.g. en/basket */
  @Prop()
  public language = (document.documentElement.lang || "de") as TLanguage;

  @State()
  private hasAdditionalBasketItems!: boolean;

  private bopUrl!: string;

  cartConfigListener = () => {
    this.refreshCartConfig();
  };

  constructor() {
    if (!this.host.slot) {
      this.host.slot = "cart";
    }
  }

  public connectedCallback() {
    this.sdxHeader = new HeaderInterface(this.host.closest("sdx-header")!);
  }

  public async componentWillLoad(): Promise<void> {
    if (!this.i18n) {
      this.i18n = await bundleForLanguage(this.language);
    }

    this.sdxHeader.updateSlot(this.host.slot!, {
      label: "",
    });

    document.body.addEventListener(
      CART_CONFIG_UPDATED,
      this.cartConfigListener,
    );

    await this.refreshCartConfig();
  }

  async refreshCartConfig() {
    await this.updateBopUrl(
      getValidatedValueFromLocalStorage(CART_CONFIG_LOCAL_STORAGE_KEY),
    );
    this.sdxHeader.updateSlot(this.host.slot!, {
      badge: this.hasAdditionalBasketItems ? " " : "",
      label: "",
      dataset: SLOT_DATASET,
      iconName: ICON,
      href: this.bopUrl,
    });
  }

  public async componentWillUpdate(): Promise<void> {
    if (this.language !== this.i18n.lang) {
      this.i18n = await bundleForLanguage(this.language);
      await this.refreshCartConfig();
    }
    this.sdxHeader.updateSlot(this.host.slot!, {
      label: "",
    });
  }

  public disconnectedCallback() {
    document.body.removeEventListener(
      CART_CONFIG_UPDATED,
      this.cartConfigListener,
    );
  }

  private async updateBopUrl(newBopUrl: string | null): Promise<void> {
    if (!this.i18n) {
      this.i18n = await bundleForLanguage(this.language);
    }

    let bopHref = `/${this.language}/${this.i18n.t("BOP_PATH")}`;
    let hasAdditionalBasketItems = false;

    if (newBopUrl) {
      const bopUrl = new URL(newBopUrl, window.location.href);
      if (
        bopUrl.origin === window.location.origin ||
        bopUrl.origin.endsWith(".swisscom.ch") ||
        bopUrl.origin.endsWith("//swisscom.ch")
      ) {
        bopHref = newBopUrl;
        hasAdditionalBasketItems =
          bopUrl.searchParams.has("abo") ||
          bopUrl.searchParams.has("deviceVariant") ||
          bopUrl.searchParams.has("exchangeId") ||
          bopUrl.searchParams.has("basketExchangeId");
      } else {
        if (devMode) {
          console.error("Invalid BOP URL detected in local storage", bopUrl);
        }
      }
    }

    this.bopUrl = bopHref;
    this.hasAdditionalBasketItems = hasAdditionalBasketItems;
  }
}
